import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { getAgreementsHistory } from "../../services/agreementServices";
import FileChangesViewHistory from "../File/FileChangeViewHistory";
import CloseIcon from "@mui/icons-material/Close";


const parseJsonField = (value) => {
  try {
    if (Array.isArray(value)) return value; // ya es un array
    if (typeof value === "object" && value !== null) return [value]; // es un objeto, lo envolvemos en array

    // Ahora asumimos que es un string
    const cleanedValue = value.replace(/^"|"$/g, "");
    const unescapedValue = cleanedValue.replace(/\\"/g, '"');
    return JSON.parse(unescapedValue);
  } catch (error) {
    console.error("Error al parsear JSON:", error);
    return [];
  }
};

const AgreementHistory = ({ extraButtons }) => {
  const { agreementId } = useParams();
  const [agreementHistory, setAgreementHistory] = useState([]);
  const [open, setOpen] = useState(false);

  const [page, setPage] = useState(1);
  //const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const buttonRef = useRef(null);
 

  const fetchFirstPage = useCallback(async () => {
    if (!agreementId) return;
  
    setIsLoading(true);
    try {
      const response = await getAgreementsHistory(agreementId, 1);
      const newVersions = response.data.data;
      setAgreementHistory(newVersions);
  
      const totalPagesFromApi = response.data.meta.total_pages;
      if (1 >= totalPagesFromApi) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      setPage(1); // actualizamos página
    } catch (err) {
      console.error("Error al obtener historial:", err);
    } finally {
      setIsLoading(false);
    }
  }, [agreementId]);
  
  

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !hasMore || page === 1) return;

      setIsLoading(true);
      try {
        const response = await getAgreementsHistory(agreementId, page);
        const newVersions = response.data.data;

        setAgreementHistory((prev) => {
          const ids = new Set(prev.map((v) => v.id));
          const filtered = newVersions.filter((v) => !ids.has(v.id));
          return [...prev, ...filtered];
        });

        const totalPagesFromApi = response.data.meta.total_pages;
        //setTotalPages(totalPagesFromApi);

        if (page >= totalPagesFromApi) {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error al obtener historial:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, open, agreementId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !isLoading && hasMore && open) {
          setPage((prev) => prev + 1);
          
        }
      },
      {
        root: null,
        threshold: 1.0,
      }
    );

    const current = loaderRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [open, hasMore, isLoading]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 100);
  };
  
  const handleOpen = () => {
    setAgreementHistory([]);
    setPage(1);
    setHasMore(true);
    setOpen(true);
    fetchFirstPage(); // Llamamos directamente a la carga
  };
  
  
  
  

  return (
    <div>
      <Grid container justifyContent="flex-end" >
        {extraButtons}
        <Button variant="outlined" onClick={handleOpen} ref={buttonRef} sx={{ ml: 2 }}>
          Historial
        </Button>
      </Grid>

      <Modal open={open} onClose={handleClose} key={open ? "open" : "closed"}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            outline: "none",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Historial de cambios</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ minHeight: 500 }}>
            {agreementHistory?.map((agreement) => (
              <Card key={agreement.id} variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Acción: {agreement.event} | Usuario: {agreement.whodunnit}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Fecha:{" "}
                    {new Date(agreement.created_at).toLocaleString("es-ES")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {Array.isArray(agreement.changeset) &&
                  agreement.changeset.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Campo</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Antes</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Después</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(agreement.changeset || []).map((change, index) => {
                          if (change.field === "signers") {
                            const oldSigners = parseJsonField(change.old_value);
                            const newSigners = parseJsonField(change.new_value);
                            return (
                              <TableRow key={index}>
                                <TableCell>Firmantes</TableCell>
                                <TableCell>
                                  {oldSigners.length > 0
                                    ? oldSigners.map((s, i) => (
                                        <div key={i}>
                                          <strong>Nombre:</strong> {s.name}
                                          <br />
                                          <strong>Posición:</strong>{" "}
                                          {s.position}
                                        </div>
                                      ))
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  {newSigners.length > 0
                                    ? newSigners.map((s, i) => (
                                        <div key={i}>
                                          <strong>Nombre:</strong> {s.name}
                                          <br />
                                          <strong>Posición:</strong>{" "}
                                          {s.position}
                                        </div>
                                      ))
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          if (change.field === "coordinators") {
                            const oldCoords = parseJsonField(change.old_value);
                            const newCoords = parseJsonField(change.new_value);

                            return (
                              <TableRow key={index}>
                                <TableCell>Coordinadores</TableCell>
                                <TableCell>
                                  {oldCoords.length > 0
                                    ? oldCoords.map((c, i) => (
                                        <div key={i}>
                                          <strong>Nombre:</strong> {c.name}
                                          {c.position && (
                                            <>
                                              <br />
                                              <strong>Posición:</strong>{" "}
                                              {c.position}
                                            </>
                                          )}
                                        </div>
                                      ))
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  {newCoords.length > 0
                                    ? newCoords.map((c, i) => (
                                        <div key={i}>
                                          <strong>Nombre:</strong> {c.name}
                                          {c.position && (
                                            <>
                                              <br />
                                              <strong>Posición:</strong>{" "}
                                              {c.position}
                                            </>
                                          )}
                                        </div>
                                      ))
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          

                          if (change.field === "attached_file_names") {
                            return (
                              <TableRow key={index}>
                                <TableCell>Archivos adjuntos</TableCell>
                                <TableCell>
                                  <FileChangesViewHistory
                                    attachedFileNames={[
                                      change.old_value,
                                      change.new_value,
                                    ]}
                                    view="before"
                                  />
                                </TableCell>
                                <TableCell>
                                  <FileChangesViewHistory
                                    attachedFileNames={[
                                      change.old_value,
                                      change.new_value,
                                    ]}
                                    view="after"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          }

                          const oldVal =
                            change.field === "status"
                              ? change.old_value
                              : change.old_value || "—";
                          const newVal =
                            change.field === "status"
                              ? change.new_value
                              : change.new_value || "—";

                          return (
                            <TableRow key={index}>
                              <TableCell>{change.field_translated}</TableCell>
                              <TableCell>{oldVal}</TableCell>
                              <TableCell>{newVal}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2">Sin cambios</Typography>
                  )}
                </CardContent>
              </Card>
            ))}
            <div ref={loaderRef} style={{ height: 40 }} />
          </Box>

          <Box>
            <Button onClick={handleClose}>Cerrar</Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default AgreementHistory;
